import { supabase } from './lib/supabase';
import { loadStripe } from '@stripe/stripe-js';
import { sendOrderNotifications } from './orderNotificationService';

// Chave pública do Stripe obtida das variáveis de ambiente
// @ts-ignore
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

export interface CreateCheckoutParams {
  event_id: string;
  batch_index: number;
  quantity: number;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  client_id?: string;
  payment_method?: string;
  convenience_fee?: number;
  convenience_fee_percentage?: number;
  success_url?: string;
  cancel_url?: string;
}

export interface CheckoutResponse {
  sessionId?: string;
  url?: string;
  orderId?: string;
  error?: string;
}

export interface EventOrder {
  id: string;
  event_id: string;
  client_id?: string;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  client_document?: string;
  stripe_session_id?: string;
  stripe_payment_intent_id?: string;
  amount_total: number;
  currency: string;
  quantity: number;
  batch_index: number;
  batch_name?: string;
  payment_method?: string;
  convenience_fee?: number;
  convenience_fee_percentage?: number;
  payment_proof_url?: string;
  payment_url?: string;
  checkout_url?: string;
  status: 'pending' | 'paid' | 'canceled' | 'failed' | 'pending_proof' | 'approved' | string;
  created_at: string;
}

export interface EventTicket {
  id: string;
  order_id: string;
  event_id: string;
  client_id?: string;
  ticket_number: number;
  qr_code_hash: string;
  status: 'valid' | 'used' | 'canceled';
  used_at?: string;
  created_at: string;
  client?: {
    id?: string;
    nome?: string;
    documento?: string;
    whatsapp?: string;
    email?: string;
  };
}

/**
 * Cria a sessão do Stripe Checkout via Supabase Edge Function e inicia o redirecionamento.
 */
export const createCheckoutSession = async (params: CreateCheckoutParams): Promise<CheckoutResponse> => {
  try {
    // 1. Invocar a Edge Function no Supabase
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: params,
    });

    if (error) {
      console.error('Erro ao invocar Edge Function create-checkout-session:', error);
      return { error: error.message || 'Falha ao conectar com o serviço de pagamento.' };
    }

    if (data?.error) {
      return { error: data.error };
    }

    // 2. Se a Edge Function retornou a URL direta do Checkout, redireciona o usuário
    if (data?.url) {
      window.location.href = data.url;
      return data;
    }

    // 3. Fallback: Se retornou sessionId e a chave pública está configurada
    if (data?.sessionId && stripePromise) {
      const stripe = await stripePromise;
      if (stripe) {
        // @ts-ignore
        const { error: stripeError } = await stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });
        if (stripeError) {
          return { error: stripeError.message };
        }
      }
    }

    return data || { error: 'Não foi possível redirecionar para a página de checkout.' };
  } catch (err: any) {
    console.error('Erro no stripeService:', err);
    return { error: err.message || 'Erro inesperado no checkout.' };
  }
};

/**
 * Busca os detalhes do pedido pelo id ou stripe_session_id ou mercadopago_preference_id
 */
export const getOrderBySessionId = async (idOrSessionId: string): Promise<EventOrder | null> => {
  try {
    if (!idOrSessionId) return null;
    const cleanId = idOrSessionId.trim();

    // 1. Tenta buscar diretamente por ID (UUID da ordem)
    let { data, error } = await supabase
      .from('app_event_orders')
      .select('*')
      .eq('id', cleanId)
      .maybeSingle();

    // 2. Se não achou por ID, tenta pelos campos de sessão / preferência / pagamento
    if (!data) {
      const fallbackQuery = await supabase
        .from('app_event_orders')
        .select('*')
        .or(`stripe_session_id.eq.${cleanId},mercadopago_preference_id.eq.${cleanId},mercadopago_payment_id.eq.${cleanId}`)
        .order('created_at', { ascending: false })
        .limit(1);

      if (fallbackQuery.data && fallbackQuery.data.length > 0) {
        data = fallbackQuery.data[0];
      }
    }

    if (!data) {
      console.warn('Pedido não encontrado por ID ou SessionId:', cleanId);
      return null;
    }

    return data as EventOrder;
  } catch (err) {
    console.error('Erro ao consultar pedido:', err);
    return null;
  }
};

/**
 * Busca os ingressos gerados para um pedido (e gera caso ainda não existam)
 */
export const getTicketsByOrderId = async (orderId: string, orderFallbackData?: EventOrder): Promise<EventTicket[]> => {
  try {
    if (!orderId) return [];

    // 1. Busca os tickets já cadastrados trazendo os dados do titular na app_people
    const { data: tickets, error } = await supabase
      .from('app_event_tickets')
      .select('*, client:app_people(id, nome, documento, whatsapp, email)')
      .eq('order_id', orderId)
      .order('ticket_number', { ascending: true });

    let order = orderFallbackData;
    if (!order) {
      const { data: ord } = await supabase
        .from('app_event_orders')
        .select('*')
        .eq('id', orderId)
        .maybeSingle();
      order = ord as EventOrder;
    }

    if (!error && tickets && tickets.length > 0) {
      // Garantir que todos os tickets com client_id tenham seus dados de pessoa carregados
      const missingClientIds = tickets
        .map((t: any) => t.client_id)
        .filter((id: string, idx: number, arr: string[]) =>
          Boolean(id) &&
          !((tickets[idx] as any)?.client?.nome || (tickets[idx] as any)?.person?.nome) &&
          arr.indexOf(id) === idx
        );

      let fetchedPeopleMap: Record<string, any> = {};
      if (missingClientIds.length > 0) {
        const { data: peopleData } = await supabase
          .from('app_people')
          .select('id, nome, documento, whatsapp, email')
          .in('id', missingClientIds);
        (peopleData || []).forEach((p: any) => {
          fetchedPeopleMap[p.id] = p;
        });
      }

      let attendees: any[] = [];
      if ((order as any)?.cancellation_reason && typeof (order as any).cancellation_reason === 'string' && (order as any).cancellation_reason.trim().startsWith('[')) {
        try {
          const parsed = JSON.parse((order as any).cancellation_reason);
          if (Array.isArray(parsed)) attendees = parsed;
        } catch {}
      }

      const enrichedTickets = tickets.map((t: any, idx: number) => {
        const directPerson = t.client || t.person || (t.client_id ? fetchedPeopleMap[t.client_id] : null);

        // Se o ticket possui titular vinculado diretamente que é diferente do comprador (ex: transferido nominalmente)
        const isExplicitHolder = directPerson && directPerson.nome && (
          (t.client_id && order?.client_id && t.client_id !== order.client_id) ||
          (!order?.client_id && t.client_id)
        );

        if (isExplicitHolder) {
          return {
            ...t,
            client: {
              id: directPerson.id || t.client_id,
              nome: directPerson.nome,
              documento: directPerson.documento || undefined,
              whatsapp: directPerson.whatsapp || undefined,
              email: directPerson.email || undefined,
            }
          };
        }

        const att = attendees[idx] || null;
        if (att) {
          return {
            ...t,
            client: {
              id: directPerson?.id || t.client_id || att.person_id || att.client_id,
              nome: (directPerson?.nome && t.client_id !== order?.client_id ? directPerson.nome : null) || att.nome || directPerson?.nome || (idx === 0 ? order?.client_name : `Participante ${idx + 1}`),
              documento: (directPerson?.documento && t.client_id !== order?.client_id ? directPerson.documento : null) || att.documento || att.cpf || directPerson?.documento || (idx === 0 ? (order as any)?.client_document : undefined),
              whatsapp: (directPerson?.whatsapp && t.client_id !== order?.client_id ? directPerson.whatsapp : null) || att.whatsapp || att.telefone || directPerson?.whatsapp || (idx === 0 ? (order as any)?.client_phone : undefined),
              email: (directPerson?.email && t.client_id !== order?.client_id ? directPerson.email : null) || att.email || directPerson?.email || (idx === 0 ? (order as any)?.client_email : undefined),
            }
          };
        }

        if (idx === 0) {
          return {
            ...t,
            client: {
              id: directPerson?.id || t.client_id || order?.client_id,
              nome: directPerson?.nome || order?.client_name,
              documento: directPerson?.documento || (order as any)?.client_document,
              whatsapp: directPerson?.whatsapp || (order as any)?.client_phone,
              email: directPerson?.email || (order as any)?.client_email,
            }
          };
        }

        return {
          ...t,
          client: directPerson || undefined
        };
      });

      return enrichedTickets as EventTicket[];
    }

    // 2. Se ainda não existirem tickets, gera automaticamente os ingressos APENAS se a ordem estiver paga/aprovada
    if (order && (order.status === 'paid' || order.status === 'approved')) {
      const qty = order.quantity || 1;
      const ticketsToInsert: any[] = [];

      let attendees: any[] = [];
      if ((order as any).cancellation_reason) {
        try {
          const parsed = JSON.parse((order as any).cancellation_reason);
          if (Array.isArray(parsed)) attendees = parsed;
        } catch {}
      }

      for (let i = 0; i < qty; i++) {
        const qrHash = `BN-${order.event_id?.slice(0, 8) || 'EV'}-${order.id.slice(0, 8)}-${i + 1}-${Date.now().toString(36).toUpperCase()}`;
        const att = attendees[i] || null;
        const attendeeClientId = att?.person_id || att?.client_id || (i === 0 ? order.client_id : null);

        ticketsToInsert.push({
          order_id: order.id,
          event_id: order.event_id,
          client_id: attendeeClientId || null,
          ticket_number: i + 1,
          qr_code_hash: qrHash,
          status: 'valid',
          created_at: new Date().toISOString(),
        });
      }

      // Upsert seguro contra concorrência
      const { error: insertErr } = await supabase
        .from('app_event_tickets')
        .upsert(ticketsToInsert, { onConflict: 'order_id,ticket_number', ignoreDuplicates: true });

      if (insertErr) {
        console.warn('Aviso ao persistir ingressos no banco:', insertErr.message);
      }

      // Reconsulta os tickets oficiais no banco
      const { data: finalTickets } = await supabase
        .from('app_event_tickets')
        .select('*, client:app_people(id, nome, documento, whatsapp, email)')
        .eq('order_id', orderId)
        .order('ticket_number', { ascending: true });

      if (finalTickets && finalTickets.length > 0) {
        return finalTickets as EventTicket[];
      }

      return ticketsToInsert.map((t, idx) => ({
        id: `local-${order.id}-${idx + 1}`,
        ...t
      })) as EventTicket[];
    }

    return [];
  } catch (err) {
    console.error('Erro ao consultar/gerar ingressos:', err);
    return [];
  }
};

/**
 * Cria um pedido via Pix Chave e salva com status 'pending_proof'
 */
export const createPixChaveOrder = async (orderData: {
  event_id: string;
  batch_index: number;
  batch_name?: string;
  quantity: number;
  amount_total: number;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  client_id?: string;
  payment_proof_url?: string;
  convenience_fee?: number;
  convenience_fee_percentage?: number;
  coupon_id?: string | null;
  coupon_code?: string | null;
  discount_amount?: number;
  client_document?: string | null;
}): Promise<{ data?: EventOrder; error?: string }> => {
  try {
    const payload = {
      event_id: orderData.event_id,
      client_id: orderData.client_id || null,
      client_name: orderData.client_name || null,
      client_email: orderData.client_email || null,
      client_phone: orderData.client_phone || null,
      client_document: orderData.client_document || null,
      amount_total: orderData.amount_total,
      currency: 'brl',
      quantity: orderData.quantity,
      batch_index: orderData.batch_index,
      batch_name: orderData.batch_name || null,
      status: 'pending_proof',
      payment_method: 'pix_chave',
      payment_proof_url: orderData.payment_proof_url || null,
      convenience_fee: orderData.convenience_fee || 0,
      convenience_fee_percentage: orderData.convenience_fee_percentage || 0,
      coupon_id: orderData.coupon_id || null,
      coupon_code: orderData.coupon_code || null,
      discount_amount: orderData.discount_amount || 0,
    };

    const { data, error } = await supabase
      .from('app_event_orders')
      .insert([payload])
      .select('*')
      .single();

    if (error) {
      console.error('Erro ao registrar pedido Pix Chave:', error);
      return { error: error.message || 'Erro ao registrar pedido.' };
    }

    if (data?.id) {
      // Disparar notificações automáticas de Pedido Criado (WhatsApp + E-mail)
      sendOrderNotifications({
        type: 'created',
        orderId: data.id,
        orderData: data,
      }).catch(() => {});
    }

    return { data: data as EventOrder };
  } catch (err: any) {
    console.error('Erro ao criar pedido Pix Chave:', err);
    return { error: err.message || 'Erro inesperado ao salvar pedido.' };
  }
};

/**
 * Upload de comprovante de pagamento Pix para o Supabase Storage
 */
export const uploadPaymentProof = async (file: File, eventId: string): Promise<{ url?: string; error?: string }> => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `proofs/${eventId}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('events')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      // Se der erro no bucket 'events', tentar converter para dataURL como fallback
      console.warn('Falha no upload para storage, usando conversão local:', uploadError);
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ url: reader.result as string });
        reader.onerror = () => resolve({ error: 'Erro ao processar imagem do comprovante' });
        reader.readAsDataURL(file);
      });
    }

    const { data: { publicUrl } } = supabase.storage
      .from('events')
      .getPublicUrl(fileName);

    return { url: publicUrl };
  } catch (err: any) {
    console.error('Erro no upload do comprovante:', err);
    return { error: err.message || 'Erro ao enviar comprovante.' };
  }
};

